import {
  FIVE9_WEBCHAT_VISITOR_TYPES,
  getVisitorType,
} from './five9VisitorTypes';
import { OPTIMIZELY_DATAFILE_ID } from './env';
import optimizelySDK from '@optimizely/optimizely-sdk';
import { getCookie } from './cookieUtils';

optimizelySDK.setLogger(null);

export const FIVE9_WEBCHAT_OPEN_CLASSNAME = 'open-chat';

const optimizelyUserIdCookieName = 'optimizelyEndUserId';

/**
 * Determine if current device supports local storage
 *
 * @returns {boolean} if current device supports local storage
 */
const hasLocalStorage = () => {
  try {
    window.localStorage.setItem('test', 'test');
    window.localStorage.removeItem('test');
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Using the five9 local storage data, determine if the chat widget is in progress
 *
 * @returns {boolean} if Five9 chat is active in local storage
 */
const isFive9ChatInProgress = () => {
  let active = false;
  if (hasLocalStorage()) {
    for (let i = 0; i < localStorage.length; ++i) {
      let key = window.localStorage.key(i);
      // the localStorage key for five9 has some random characters on the end, so use startWith to find it
      if (key.startsWith('f9-chat-console')) {
        let f9State;

        // attempt to convert the stored string into a json object
        try {
          f9State = JSON.parse(window.localStorage.getItem(key));
          active = true;
        } catch (error) {
          f9State = {};
        }

        // if we have no session id OR we do have a session ID but the "step" is "Finished", hide the chat
        if (
          !f9State ||
          !f9State.session ||
          !f9State.session.id ||
          f9State.session.id.length == 0 ||
          f9State.step == 'Finished'
        ) {
          active = false;
        }
      }
    }
  }
  return active;
};

/**
 * Whether WebChat has been marked as available/unavailable by Content Producers
 *
 * @returns {Promise<Boolean>}
 */
export const isOnlineChatAvailable = async () => {
  const updateIntervalMs = 5 * 60 * 1000; // 5 minutes in milliseconds
  const optimizelyClientInstance = optimizelySDK.createInstance({
    autoUpdate: true,
    updateInterval: updateIntervalMs,
    sdkKey: OPTIMIZELY_DATAFILE_ID,
  });

  const success = await optimizelyClientInstance
    .onReady({ timeout: 10000 })
    .then((result) => {
      // if optimizely datafile download failed (ie. takes more than 10 seconds, network error), be optimistic and say online chat is available
      if (!result.success) {
        return true;
      }
      let featureToCheck = {
        [FIVE9_WEBCHAT_VISITOR_TYPES.default]: 'online_chat',
        [FIVE9_WEBCHAT_VISITOR_TYPES.easybiz]: 'online_chat_easybiz',
        [FIVE9_WEBCHAT_VISITOR_TYPES.travelAgent]: 'online_chat_travel_agent',
      }[getVisitorType()];

      const userId = getCookie(optimizelyUserIdCookieName);

      // if there is no optimizely cookie, be optimistic and say online chat is available
      return userId
        ? optimizelyClientInstance.isFeatureEnabled(featureToCheck, userId)
        : true;
    });
  return success;
};

/**
 * Check page DOM for an element with the className of `open-chat` to know if chat can be started from here
 *
 * @returns {boolean}
 */
export const isOnlineChatInitializerOnPage = () => {
  const chatOpeningElements = document.body.getElementsByClassName(
    FIVE9_WEBCHAT_OPEN_CLASSNAME
  );
  return chatOpeningElements.length > 0;
};

/**
 * Whether the client should bother loading in the online chat module on a page
 *
 * @returns {Promise<Boolean>}
 */
export const shouldLoadOnlineChat = async () =>
  isFive9ChatInProgress() ||
  (isOnlineChatInitializerOnPage() && (await isOnlineChatAvailable()));
